import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import i18n from "i18n";

import { useCast } from "hooks";
import { GAHandler } from "utils/GA";
import { getThumbnail } from "utils/thumbnails";
import { parseTime } from "utils/common";

import Player from "./Player";
import Controls from "./Controls";
import NextEpisode from "./NextEpisode";
import Subtitles from "./Subtitles";

import { Container } from "./styles";


const defaultConfig = {
  afkTimeoutClose: true,
  afkTimeoutTime: 20000, // 20 seconds
  nextEpisode: true,
  events: {},
  mode: 'content', // content | ad
}


const PlayerLanding = ({
  video,
  onClose,
  nextEpisode,
  onNextEpisode,
  title,
  description,
  config: customConfig,
  onEnd,
  loaderEl: Loader
}) => {

  const config = {    ...defaultConfig,
    ...customConfig,
    events: {
      ...defaultConfig.events,
      ...customConfig.events,
    },
    player: {
      ...defaultConfig.player,
      ...customConfig.player,
    },
  }

  const { t } = useTranslation()
  const { cast } = useCast()

  const afkTimeoutRef = useRef()
  const playerRef = useRef()


  const [weblink, setWeblink] = useState()
  const [showNextEp, setShowNextEp] = useState(false)
  const [isCasting, setIsCasting] = useState(false)

  // PLAYER
  const [isFirstPlay, setIsFirstPlay] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(video?.duration || 0);

  // CONTROLS
  const [areControlsVisible, setAreControlsVisible] = useState(false);

  // SUBTITLES
  const [videoSubtitles, setVideoSubtitles] = useState([])
  const [currentSubtitle, setCurrentSubtitle] = useState('');

  // DERIVATED STATES
  const src = video.src || weblink
  const timeLeft = duration - played
  const hasSubtitles = !!video?.subtitle?.length

  const presentNextEpisode = config.nextEpisode && nextEpisode && showNextEp && !isCasting

  // const handleShowNextEp = (e) => {
  //   if (!nextEpisode) return
  //   setShowNextEp(e.timeLeft <= 10)
  // }

  const handleCTANextEpisode = () => {
    setWeblink(null)
    onNextEpisode()
    // setShowNextEp(false)
    clearTimeout(afkTimeoutRef.current)
    GAHandler('ui_player_nextepisode');
    setPlayed(0)
    setDuration(0)
    setShowNextEp(false)
  }

  const handleTimeUpdate = (timePlayed) => {
    setShowNextEp(timeLeft < 11)
    setPlayed(timePlayed)
  }

  const handleEnd = () => {
    // if (cast.isRunning) {
    //   onClose()
    //   cast.stop()
    //   return
    // }

    GAHandler(config.events.onEnd);

    onEnd?.()

    if (!config.afkTimeoutClose) return

    afkTimeoutRef.current = setTimeout(() => {
      onClose({ selectNext: true })
      GAHandler('ui_player_close_inactivity');
    }, config.afkTimeoutTime);
  }

  const handleCast = () => {
    handlePause()
    cast.launch(src, played);
    GAHandler('ui_player_cast');
  }
 
  const handlePlay = ({ isFirstPlay: isFirstPlayValue } = {}) => {
    setIsFirstPlay(false)
    if (isFirstPlayValue) GAHandler(config.events.onPlay);

    setIsPlaying(true)
    if (playerRef.current) playerRef.current.play()
    if (cast.isRunning) cast.play()
  }

  const handlePause = () => {
    setIsPlaying(false)
    if (playerRef.current) playerRef.current.pause()
    if (cast.isRunning) cast.pause()
  }

  const handleScrub = (seconds) => {
    // setLoader(!pause);

    if (playerRef.current) playerRef.current.currentTime = seconds
    setPlayed(seconds);

    if (cast.isRunning) cast.seek(seconds)
  };

  const handleVolume = (value) => {
    setVolume(value)
    if (playerRef.current) playerRef.current.volume = value
    if (cast.isRunning) cast.volume(value)
  }

  const handleClose = () => {
    if (cast.isRunning) cast.stop()

    GAHandler(config.events.onUnfinished);
    clearTimeout(afkTimeoutRef.current)

    onClose()
  }

  const handleSubsPosition = (areVisible) => {
    setAreControlsVisible(areVisible)
  }

  const getSubtitles = async (sub) => {
    const response = await Axios.get(sub.url);

    const trackBlob = new Blob(
      [response.data],
      { type: "text/plain;charset=utf-8" }
    );

    const trackUrl = URL.createObjectURL(trackBlob);

    const isSpanish = sub.lang === 'es' || sub.lang === 'sp'

    return {
      uuid: sub.uuid,
      src: trackUrl,
      srcLang: sub.lang,
      label: sub.label || isSpanish ? t('spanish') : t('english'),
      default: sub.lang === i18n.language,
      closeCaption: sub.closeCaption,
      kind: sub.closeCaption ? 'captions' : 'subtitles',
    };
  }


  useEffect(() => {
    setIsCasting(cast.isRunning)
  }, [cast.isRunning])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => document.body.style.overflow = 'visible'
  }, []);

  useEffect(() => {
    if (!hasSubtitles) return

    const subtitlesArray = video.subtitle.map(getSubtitles);

    Promise.all(subtitlesArray).then(
      (subs) => {
        setVideoSubtitles(subs)

        setCurrentSubtitle(()=> (
          subs.find(sub => sub.default)?.srcLang
          || subs.find(sub => sub.srcLang === 'es' || sub.srcLang === 'sp')?.srcLang
        ))
      }
    );

    // eslint-disable-next-line
  }, [video])


  // useEffect(() => {
  //   if (isWaiting) return

  //   if (video.hasDRM) {
  //     setWeblink('...')
  //   } else if (!video.src) {
  //     apiFetch({ url: `/contents/${video.uuid}/weblink` })
  //       .then(res => setWeblink(res.data))
  //   }
  //   // eslint-disable-next-line
  // }, [video])

  return (
    <Container>

      <Subtitles.PlayerWrapper
        playerRef={playerRef}
        areControlsVisible={areControlsVisible}
      >
        <Player
          playerRef={playerRef}
          uuid={video.uuid}
          hasDrm={video.hasDrm}
          thumbnail={getThumbnail(video)}
          src={src}
          onClose={onClose}
          onEnd={handleEnd}
          title={title}
          description={description}
          setIsCasting={setIsCasting}
          onTimeUpdate={handleTimeUpdate}
          onDuration={setDuration}
          onPlay={handlePlay}
          firstLoader={Loader}
        >
          <Subtitles.Tracks
            subtitles={videoSubtitles}
            currentSubtitle={currentSubtitle}
          />
        </Player>
      </Subtitles.PlayerWrapper>

      <Controls
        mode={config.mode}
        title={title}
        description={description}
        isPlaying={isPlaying}
        volume={volume}
        currentSubtitle={currentSubtitle}
        subtitles={videoSubtitles}
        played={played}
        duration={Math.trunc(duration)}
        timeLeft={parseTime(timeLeft)}
        onVolume={handleVolume}
        onSubtitles={setCurrentSubtitle}
        onScrub={handleScrub}
        onPlay={handlePlay}
        onPause={handlePause}
        onClose={handleClose}
        onCast={handleCast}
        onShowControls={handleSubsPosition}
        isCastAvailable={cast.isAvailable}
        isFirstPlay={isFirstPlay}
      />

      {presentNextEpisode && (
        <NextEpisode
          episode={nextEpisode}
          onPlay={handleCTANextEpisode}
        />
      )}

    </Container>
  );
};


export default PlayerLanding;
